import { useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { RootState } from "../../../app/rootReducer";
import { useSelector } from "../../../app/helpers";
import { commonLocalization } from "../../../functions/tableLocalization";
import { useRemoteFetchRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import { useRemoteCreateRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteCreateRoomlayout";
import { useRemoteDeleteRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteDeleteRoomlayout";
import { useRemoteUpdateRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteUpdateRoomlayout";
import TableDeleteConfirmDialog from "../../Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { RoomlayoutListTopToolbar } from "./Toolbar/roomlayout-list-toptoolbar";
import { RoomlayoutListRowActions } from "./roomlayout-list-row-actions";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import {
  RoomlayoutServiceRow,
  RoomlayoutServiceRowValues
} from "./Domain/Types/FacilityService-Roomlayout-row.types";
import {
  checkRoomlayoutVal,
  roomlayoutRowValuesToFormData
} from "./functions/roomlayout-list.functions";
import {
  editRoomLayoutCell,
  renderRoomLayoutCell
} from "../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/RoomLayoutDialogContent/RoomLayoutTable.partial";
import { validateRoomlayoutRow } from "./functions/roomlayout-list-validation.function";
import { mapNewRowToPartialRoomlayoutItem } from "./helpers/mapNewRowToPartialRoomlayoutItem.function";
import { mapUpdatedRowToPartialRoomlayoutItem } from "./helpers/mapUpdatedRowToPartialCateringItem.function";
import { SlidePanelCreateEditService } from "./roomlayout-list.partial";
import { generateNewRoomlayoutId } from "./helpers/generateNewRoomlayoutId.function";
import { EnlargedImageCarouselDialog } from "../../../features/EnlargedImageCarousel/enlarged-image-carousel-dialog.component";
import {
  editTableItemMultiImagesComp,
  renderTableItemMultiImages
} from "../../Common/ListTablePartial/ListTablePartial.partial";
import { normalizePicturePaths } from "./helpers/normalizePicturePaths.function";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

/**
 * @version 0.1.0
 */
export default function RoomlayoutSerivceList() {
  const {
    userInformation: { sub }
  } = useSelector((state: RootState) => state.login);
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [data, setData] = useState<RoomlayoutServiceRow[]>();

  const [imageFiles, setImageFiles] = useState<FileObject[]>([]);
  const [showBiggerImages, setShowBiggerImages] = useState<string[] | null>(null);
  const [picturePathsToDelete, setPicturePathsToDelete] = useState<string[]>([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<number[] | null>(null);

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  const {
    data: remoteRoomlayout,
    isLoading: isRemoteRoomlayoutLoading,
    refetch: refetchRemoteRoomlayout
  } = useRemoteFetchRoomlayout();

  const { mutateAsync: addRoomlayout, status: addRoomlayoutStatus } = useRemoteCreateRoomlayout();
  const { mutateAsync: updateRoomlayout, status: updateRoomlayoutStatus } =
    useRemoteUpdateRoomlayout();
  const { mutateAsync: removeRoomlayout, status: removeRoomlayoutStatus } =
    useRemoteDeleteRoomlayout();

  const column = useMemo<MRT_ColumnDef<RoomlayoutServiceRow>[]>(
    () => [
      {
        accessorKey: "layout",
        header: t("Layout"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => renderRoomLayoutCell(cell.row.original.layout, theme),
        size: 160,
        enableEditing: false,
        Edit: ({ cell, row }) => editRoomLayoutCell(cell.row.original.layout, row, theme)
      },
      {
        accessorKey: "picturePaths",
        header: t("Image"),
        enableEditing: true,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderTableItemMultiImages(
            cell.row.original.picturePaths,
            "roomlayout-image",
            100,
            setShowBiggerImages
          ),
        Edit: ({ row }) =>
          editTableItemMultiImagesComp({
            title: t("Upload room layout images"),
            rowOriginal: row.original,
            imageFiles,
            setImageFiles,
            handleDelete: picturePath => deleteSelectedImage(picturePath, row.original)
          }),
        size: 100
      },
      {
        accessorKey: "name",
        header: t("Name"),
        enableEditing: true,
        size: 200,
        muiEditTextFieldProps: {
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              name: undefined
            }),
          onChange: e => {
            checkRoomlayoutVal(
              e.currentTarget.value,
              "name",
              validationErrors,
              setValidationErrors
            );
          },
          helperText: validationErrors?.name,
          required: true,
          error: !!validationErrors?.name
        }
      },
      {
        size: 400,
        enableEditing: true,
        accessorKey: "description",
        header: t("Description"),
        muiEditTextFieldProps: {
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              description: undefined
            }),
          onChange: e => {
            checkRoomlayoutVal(
              e.currentTarget.value,
              "description",
              validationErrors,
              setValidationErrors
            );
          },
          helperText: validationErrors?.description,
          required: true,
          error: !!validationErrors?.description
        }
      }
    ],
    [validationErrors, i18n.language, imageFiles]
  );

  useEffect(() => {
    if (!remoteRoomlayout) return;

    setData(
      remoteRoomlayout.map(item => {
        return {
          id: item.id || remoteRoomlayout.length - 1,
          name: item.name,
          description: item.description,
          picturePaths: item.picturePaths ?? null,
          layout: item.arrangement
        };
      })
    );
  }, [remoteRoomlayout]);

  // after deleting a row, refresh the list
  useEffect(() => {
    if (removeRoomlayoutStatus === "success") {
      const multiDeleted =
        deletionSelected && deletionSelected.length > 1
          ? t("_deletedMultiRoomlayout")
          : t("_deletedSingleRoomlayout");

      enqueueSnackbar(t("Successfully deleted roomlayout", { unit: multiDeleted }), {
        variant: "success"
      });
      setIsDeleteModalOpen(false);
      refetchRemoteRoomlayout();
      setDeletionSelected(null);
    }
  }, [removeRoomlayoutStatus]);

  // after adding a eow, refresh the list
  useEffect(() => {
    if (addRoomlayoutStatus === "success") refetchRemoteRoomlayout();
  }, [addRoomlayoutStatus]);

  // after adding a eow, refresh the list
  useEffect(() => {
    if (updateRoomlayoutStatus === "success") refetchRemoteRoomlayout();
  }, [updateRoomlayoutStatus]);

  const resetTableCreateEdit = () => {
    setImageFiles([]);
    setValidationErrors({});
    setPicturePathsToDelete([]);
  };

  const deleteSelectedImage = (picturePath: string, rowOriginal: RoomlayoutServiceRow) => {
    setPicturePathsToDelete(prev => [...prev, picturePath]);
    if (Array.isArray(rowOriginal.picturePaths)) {
      rowOriginal.picturePaths = rowOriginal.picturePaths.filter(path => path !== picturePath);
    }
  };

  const updateRoomLayoutPicturepath = (values: RoomlayoutServiceRowValues) => {
    const upd = { ...values };

    upd.picturePaths = normalizePicturePaths(upd.picturePaths, picturePathsToDelete);
    return upd;
  };

  const updateRoomLayoutSwitching = (
    values: RoomlayoutServiceRowValues,
    rowOriginal: RoomlayoutServiceRow
  ) => {
    const upd = values;
    upd.layout = rowOriginal.layout;

    return upd;
  };

  /** deletes room layouts */
  const handleRowDelete = (rowUids: number[]) => {
    removeRoomlayout({ ids: rowUids });
    table.resetRowSelection();
  };

  /** adds a room layout */
  const handleRowAdd: MRT_TableOptions<RoomlayoutServiceRow>["onCreatingRowSave"] = async ({
    row,
    values,
    exitCreatingMode
  }) => {
    const newValidationErrors = validateRoomlayoutRow(values);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const pictureUpdated = updateRoomLayoutPicturepath(values);
    const layoutUpdated = updateRoomLayoutSwitching(pictureUpdated, row.original);
    const newVal = mapNewRowToPartialRoomlayoutItem(layoutUpdated);

    const newValueFormData = roomlayoutRowValuesToFormData(
      { ...newVal, id: generateNewRoomlayoutId(data) },
      row.original,
      sub,
      imageFiles
    );

    exitCreatingMode();
    await addRoomlayout(newValueFormData);
    table.setCreatingRow(null); //exit creating mode
    setImageFiles([]);
  };

  /** updates a room layout */
  const handleRowUpdate: MRT_TableOptions<RoomlayoutServiceRow>["onEditingRowSave"] = async ({
    row,
    values,
    exitEditingMode
  }) => {
    const newValidationErrors = validateRoomlayoutRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const pictureUpdated = updateRoomLayoutPicturepath(values);
    const layoutUpdated = updateRoomLayoutSwitching(pictureUpdated, row.original);
    const newVal = mapUpdatedRowToPartialRoomlayoutItem(layoutUpdated, row.original);

    const newValueFormData = roomlayoutRowValuesToFormData(newVal, row.original, sub, imageFiles);

    exitEditingMode();
    await updateRoomlayout(newValueFormData);
    table.setEditingRow(null); // exit editing mode
    setImageFiles([]);
  };
  const seeditMrtOptions = useSeeditMrtTableOptions<RoomlayoutServiceRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: true,
    autoResetPageIndex: false,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    enableEditing: true,
    columns: column ?? [],
    data: data ?? [],
    getRowId: row => row.id?.toString(),
    onCreatingRowCancel: resetTableCreateEdit,
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: resetTableCreateEdit,
    onEditingRowSave: handleRowUpdate,
    state: {
      isLoading: isRemoteRoomlayoutLoading,
      isSaving:
        (addRoomlayoutStatus || updateRoomlayoutStatus || removeRoomlayoutStatus) === "loading",
      showProgressBars: isRemoteRoomlayoutLoading
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15, 20, 30], variant: "outlined" },
    paginationDisplayMode: "pages",
    localization: { ...commonLocalization(t), noRecordsToDisplay: t("There are no roomlayout") },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditService
        headline={t("_addRoomlayout")}
        internalEditComponents={internalEditComponents}
        row={row}
        table={table}
      />
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <RoomlayoutListTopToolbar
          onDelete={() => {
            setIsDeleteModalOpen(true);

            const selected = table.getSelectedRowModel().rows.map(row => row.original.id);
            setDeletionSelected(selected);
          }}
          disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
          table={table}
        />
      );
    },
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEditService
        headline={t("_editRoomlayout")}
        internalEditComponents={internalEditComponents}
        row={row}
        table={table}
      />
    ),
    renderRowActions: ({ row, table }) => {
      return (
        <RoomlayoutListRowActions
          row={row}
          table={table}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeletionSelected={setDeletionSelected}
        />
      );
    }
  });

  return (
    <>
      <MaterialReactTable table={table} />

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDescNeutral", {
                title: t("_deleteConfirmRoomlayout")
              })
            : t("_tableDeletionConfirmationDesc")
        }
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        deleteStatus={removeRoomlayoutStatus}
      />

      <EnlargedImageCarouselDialog
        handleClose={() => setShowBiggerImages(null)}
        isOpen={!!showBiggerImages}
        picturePaths={showBiggerImages?.reverse() ?? null}
        isLoading={!showBiggerImages}
      />
    </>
  );
}
